import { useEffect, useState } from "react";
import { PortaledModalFrame } from "src/components/common/PortaledModalFrame";
import {
  orderDrawerPrimaryButtonClass,
  orderDrawerSecondaryButtonClass,
} from "src/constants/orderDrawerButtons";

const DEFAULT_DRAWER_WIDTH = 400;

export interface OrderDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional title for the drawer header (e.g. "Order Details") */
  title?: string;
  /** Optional width in px; default 400. Use 340–360 for a more compact panel. */
  width?: number;
}

export default function OrderDetailsDrawer({ open, onClose, children, title, width = DEFAULT_DRAWER_WIDTH }: OrderDetailsDrawerProps) {
  const [panelVisible, setPanelVisible] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      const t = requestAnimationFrame(() => {
        requestAnimationFrame(() => setPanelVisible(true));
      });
      return () => cancelAnimationFrame(t);
    } else {
      setPanelVisible(false);
      document.body.style.overflow = "";
    }
  }, [open]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={title ?? "Order details"}>
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${panelVisible ? "opacity-100" : "opacity-0"}`}
        aria-hidden
        onClick={onClose}
      />
      {/* Panel: slides in from right; subtle left inner shadow + content tint */}
      <div
        className={`absolute top-0 right-0 h-full bg-white flex flex-col transition-transform duration-200 ease-out ${panelVisible ? "translate-x-0" : "translate-x-full"}`}
        style={{
          width,
          maxWidth: "100vw",
          boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25), inset 4px 0 6px -2px rgb(0 0 0 / 0.05)",
        }}
      >
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] bg-white">
          {title && (
            <h2 className="text-lg font-semibold text-[#111827]">{title}</h2>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0 bg-[#F9FAFB]">
          {children}
        </div>
      </div>
    </div>
  );
}

export interface PrintOptionsSelection {
  restaurantCopy: boolean;
  customerCopy: boolean;
  sendToKitchen: boolean;
}

export interface PrintOptionsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (options: PrintOptionsSelection) => void;
  isSending?: boolean;
}

const defaultPrintOptions: PrintOptionsSelection = {
  restaurantCopy: false,
  customerCopy: false,
  sendToKitchen: false,
};

export function PrintOptionsModal({
  open,
  onClose,
  onConfirm,
  isSending = false,
}: PrintOptionsModalProps) {
  const [options, setOptions] = useState<PrintOptionsSelection>(defaultPrintOptions);

  useEffect(() => {
    if (open) setOptions(defaultPrintOptions);
  }, [open]);

  if (!open) return null;

  const toggleOption = (key: keyof PrintOptionsSelection) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <PortaledModalFrame
      onClose={isSending ? () => {} : onClose}
      panelClassName="max-w-md"
      panelStyle={{ maxWidth: 460 }}
    >
      <div className="px-6 py-5">
        <h2 id="modal-title" className="text-lg font-semibold text-[#111827]">
          Print / Send Options
        </h2>
        <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">
          Choose where this order should be printed or sent.
        </p>

        <div className="mt-5 space-y-3">
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-3 py-3 text-sm font-medium text-[#111827] transition-colors hover:bg-[#f9fafb]">
            <input
              type="checkbox"
              checked={options.restaurantCopy}
              onChange={() => toggleOption("restaurantCopy")}
              className="receipt-lang-check h-6 w-6 min-h-[24px] min-w-[24px] appearance-none rounded border border-[#9B9B9B] cursor-pointer"
            />
            Restaurant Copy
          </label>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-3 py-3 text-sm font-medium text-[#111827] transition-colors hover:bg-[#f9fafb]">
            <input
              type="checkbox"
              checked={options.customerCopy}
              onChange={() => toggleOption("customerCopy")}
              className="receipt-lang-check h-6 w-6 min-h-[24px] min-w-[24px] appearance-none rounded border border-[#9B9B9B] cursor-pointer"
            />
            Customer Copy
          </label>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-3 py-3 text-sm font-medium text-[#111827] transition-colors hover:bg-[#f9fafb]">
            <input
              type="checkbox"
              checked={options.sendToKitchen}
              onChange={() => toggleOption("sendToKitchen")}
              className="receipt-lang-check h-6 w-6 min-h-[24px] min-w-[24px] appearance-none rounded border border-[#9B9B9B] cursor-pointer"
            />
            Send to Kitchen
          </label>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            className={orderDrawerSecondaryButtonClass}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(options)}
            disabled={isSending}
            className={orderDrawerPrimaryButtonClass}
          >
            {isSending ? "Sending..." : "Confirm"}
          </button>
        </div>
      </div>
    </PortaledModalFrame>
  );
}
